import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AlarmClock,
    ArrowLeft,
    CalendarDays,
    CalendarClock,
    Camera,
    CheckCircle2,
    ChevronRight,
    Clock,
    ExternalLink,
    Flag,
    GripVertical,
    List,
    LocateFixed,
    Map as MapIcon,
    MapPin,
    Navigation,
    Phone,
    Route as RouteIcon,
    X
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import VisitaModal, { VisitaParadaInfo, VisitaConclusao } from './VisitaModal';
import CalendarioRotas, { DiaRotasResumo } from './CalendarioRotas';
import RemanejarLeadsModal from './RemanejarLeadsModal';
import { ParadaRemanejavel, registrarManterPendentes, remanejarParadas, remanejarRotaInteira } from '../../../src/lib/remanejamento';
import { DiaAgendaFutura, PontoSugestao, sugerirDataReagendamento } from '../../../src/lib/sugerirData';
import { gerarRotaInteligente, googleMapsDirLink, reordenarPendentesAPartirDe, GOOGLE_MAPS_MAX_STOPS } from '../../../src/lib/rotaInteligente';
import { useAgendaRealtime } from '../../../src/hooks/useAgendaRealtime';
import { concluirTarefaComResultado } from '../../../src/lib/visitaTarefa';
import type { ParadaMapa } from './RotaMapa';
import { enderecoParada, urlNavegacao } from './rotaTypes';

// Lazy: o chunk do Leaflet só carrega quando o operador abre o mapa da rota.
const RotaMapa = React.lazy(() => import('./RotaMapa'));

interface ParadaOp {
    id: string;
    posicao: number;
    status: string;
    checkin_em?: string | null;
    checkout_em?: string | null;
    foto_url?: string | null;
    descricao_visita?: string | null;
    negociacao_id?: number | null;
    tarefa_id?: number | null;
    numero_medidor?: string | null;
    ocr_status?: string | null;
    ocr_candidatos?: Array<{ numero: string; score: number }> | null;
    negociacoes?: { id: number; titulo: string; cidade?: string | null; bairro?: string | null; latitude?: number | null; longitude?: number | null; whatsapp?: string | null; segmento?: string | null; numero_medidor?: string | null; unidades_consumidoras?: Array<{ logradouro?: string | null; numero?: string | null; bairro?: string | null; cidade?: string | null; cep?: string | null }> | null } | null;
}

interface RotaOp {
    id: string;
    nome?: string | null;
    data: string;
    status: string;
    prioridade?: string | null;
    observacoes?: string | null;
    distancia_total_km?: number | null;
    duracao_min?: number | null;
    iniciada_em?: string | null;
    concluida_em?: string | null;
    rota_paradas: ParadaOp[];
}

interface VisitaDiaOp {
    id: number;
    titulo: string;
    vencimento: string;
    modalidade?: string | null;
    status: string;
    negociacao_id?: number | null;
    negociacoes?: { titulo?: string | null; cidade?: string | null; bairro?: string | null; latitude?: number | null; longitude?: number | null } | null;
}

/** Contexto do modal de remanejamento (leads selecionados, rota inteira ou alerta de expediente). */
interface RemanejoCtx {
    modo: 'leads' | 'rota';
    rotaId: string;
    titulo: string;
    dataOriginal: string;
    paradas: Array<ParadaRemanejavel & { ponto?: PontoSugestao }>;
}

const hojeIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtData = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });

const prioridadeChip = (p?: string | null) => {
    if (p === 'alta') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">🔴 Alta</span>;
    if (p === 'baixa') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">🟢 Baixa</span>;
    if (p === 'media') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">🟡 Média</span>;
    return null;
};

const statusChip = (s: string) => {
    const map: Record<string, [string, string]> = {
        agendada: ['Agendada', 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'],
        reagendada: ['Reagendada', 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'],
        planejada: ['Planejada', 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'],
        em_execucao: ['Em execução', 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'],
        concluida: ['Concluída', 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'],
        cancelada: ['Cancelada', 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'],
    };
    const [label, cls] = map[s] || [s, 'bg-gray-100 text-gray-600'];
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${cls}`}>{label}</span>;
};

const tempoTotal = (ini?: string | null, fim?: string | null) => {
    if (!ini) return null;
    const ms = (fim ? new Date(fim).getTime() : Date.now()) - new Date(ini).getTime();
    const min = Math.max(1, Math.round(ms / 60000));
    const h = Math.floor(min / 60);
    return h > 0 ? `${h}h${String(min % 60).padStart(2, '0')}` : `${min} min`;
};

/**
 * Tela do Operador de Campo (motoboy) — Fase M1.
 * Minhas Rotas (hoje / próximas / concluídas) + execução com foto do medidor + resumo final.
 */
const OperadorView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const [rotas, setRotas] = useState<RotaOp[]>([]);
    const [loading, setLoading] = useState(true);
    const [rotaAbertaId, setRotaAbertaId] = useState<string | null>(null);
    // Visitas planejadas do dia ainda não roteirizadas (fonte única: tarefas) + geração da rota inteligente
    const [visitasDia, setVisitasDia] = useState<VisitaDiaOp[]>([]);
    const [gerandoRota, setGerandoRota] = useState(false);
    const [visitaModal, setVisitaModal] = useState<{ parada: VisitaParadaInfo; etapa: 'iniciar' | 'encerrar' } | null>(null);
    const [showResumo, setShowResumo] = useState(false);
    const [mapaAberto, setMapaAberto] = useState(false);
    const [reordenando, setReordenando] = useState(false);
    useEffect(() => { setMapaAberto(false); }, [rotaAbertaId]);

    // Calendário + remanejamento (Motoboy v3)
    const [vista, setVista] = useState<'lista' | 'calendario'>('lista');
    const [mesAtual, setMesAtual] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
    const [dataSel, setDataSel] = useState(hojeIso());
    const [rotasMes, setRotasMes] = useState<RotaOp[]>([]);
    const [leadsSel, setLeadsSel] = useState<Set<string>>(new Set());
    const [remanejo, setRemanejo] = useState<RemanejoCtx | null>(null);
    const [remanejando, setRemanejando] = useState(false);
    const [arrastando, setArrastando] = useState<{ rota: RotaOp; parada: ParadaOp } | null>(null);
    // Alerta de fim de expediente (item 3)
    const [fimExpediente, setFimExpediente] = useState<string>('18:00');
    const [expedienteRota, setExpedienteRota] = useState<RotaOp | null>(null);

    const ROTA_SELECT = 'id, nome, data, status, prioridade, observacoes, distancia_total_km, duracao_min, iniciada_em, concluida_em, destino_lat, destino_lng, destino_label, rota_paradas(id, posicao, status, checkin_em, checkout_em, foto_url, descricao_visita, negociacao_id, tarefa_id, numero_medidor, ocr_status, ocr_candidatos, negociacoes(id, titulo, cidade, bairro, latitude, longitude, geocode_status, whatsapp, segmento, numero_medidor, unidades_consumidoras(logradouro, numero, bairro, cidade, cep)))';

    const fetchRotas = async () => {
        if (!profile) return;
        const { data, error } = await supabase
            .from('rotas_comerciais')
            .select(ROTA_SELECT)
            .eq('vendedor_id', profile.id)
            .neq('status', 'cancelada')
            .order('data', { ascending: false })
            .limit(30);
        if (error) { console.error(error); return; }
        const lista = (data || []) as unknown as RotaOp[];
        lista.forEach(r => r.rota_paradas.sort((a, b) => a.posicao - b.posicao));
        setRotas(lista);
    };

    /** Rotas do mês visível (+10 dias p/ drag-and-drop e sugestão de reagendamento). */
    const fetchRotasMes = async (mes: Date) => {
        if (!profile) return;
        const ini = `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, '0')}-01`;
        const fimDate = new Date(mes.getFullYear(), mes.getMonth() + 1, 10);
        const fim = `${fimDate.getFullYear()}-${String(fimDate.getMonth() + 1).padStart(2, '0')}-${String(fimDate.getDate()).padStart(2, '0')}`;
        const { data, error } = await supabase
            .from('rotas_comerciais')
            .select(ROTA_SELECT)
            .eq('vendedor_id', profile.id)
            .neq('status', 'cancelada')
            .gte('data', ini)
            .lte('data', fim)
            .order('data', { ascending: true });
        if (error) { console.error(error); return; }
        const lista = (data || []) as unknown as RotaOp[];
        lista.forEach(r => r.rota_paradas.sort((a, b) => a.posicao - b.posicao));
        setRotasMes(lista);
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await fetchRotas();
            const { data: cfg } = await supabase.from('system_settings').select('value').eq('key', 'rotas_fim_expediente').maybeSingle();
            if (cfg?.value) setFimExpediente(cfg.value);
            setLoading(false);
        };
        if (profile) init();
    }, [profile?.id]);

    useEffect(() => {
        if (vista === 'calendario' && profile) fetchRotasMes(mesAtual);
    }, [vista, mesAtual, profile?.id]);

    const hoje = hojeIso();
    const grupos = useMemo(() => ({
        emExecucao: rotas.filter(r => r.status === 'em_execucao'),
        doDia: rotas.filter(r => r.data === hoje && ['planejada', 'agendada', 'reagendada'].includes(r.status)),
        futuras: rotas.filter(r => r.data > hoje && ['planejada', 'agendada', 'reagendada'].includes(r.status)).sort((a, b) => a.data.localeCompare(b.data)),
        concluidas: rotas.filter(r => r.status === 'concluida').slice(0, 10),
    }), [rotas, hoje]);

    const rotaAberta = rotas.find(r => r.id === rotaAbertaId) || null;

    const getGeo = (): Promise<{ lat: number; lng: number } | undefined> =>
        new Promise(resolve => {
            if (!navigator.geolocation) return resolve(undefined);
            navigator.geolocation.getCurrentPosition(
                pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                () => resolve(undefined),
                { timeout: 4000 }
            );
        });

    // ── Rota Inteligente (paridade com o CRM): visitas do dia → TSP a partir do meu GPS ──
    const diaAlvo = vista === 'calendario' ? dataSel : hojeIso();

    const fetchVisitasDia = async (iso: string) => {
        if (!profile) return;
        const { data, error } = await supabase
            .from('tarefas')
            .select('id, titulo, vencimento, modalidade, status, negociacao_id, negociacoes(titulo, cidade, bairro, latitude, longitude)')
            .eq('responsavel_id', profile.id)
            .eq('vencimento', iso)
            .in('status', ['a_iniciar', 'em_andamento', 'aguardando'])
            .eq('modalidade', 'presencial');
        if (error) { console.error(error); return; }
        setVisitasDia((data || []) as unknown as VisitaDiaOp[]);
    };

    // Tarefas já vinculadas a uma parada (não entram no "a rotear")
    const tarefaIdsEmRota = useMemo(() => {
        const s = new Set<number>();
        for (const r of rotas) for (const p of r.rota_paradas) if (p.tarefa_id != null) s.add(Number(p.tarefa_id));
        return s;
    }, [rotas]);

    const visitasARotear = useMemo(
        () => visitasDia.filter(v => v.negociacoes?.latitude && v.negociacoes?.longitude && !tarefaIdsEmRota.has(v.id)),
        [visitasDia, tarefaIdsEmRota]
    );

    useEffect(() => { if (profile) fetchVisitasDia(diaAlvo); /* eslint-disable-next-line */ }, [profile?.id, diaAlvo, rotas]);

    const criarRotaInteligenteDia = async (iso: string) => {
        if (!profile || visitasARotear.length === 0) return;
        setGerandoRota(true);
        try {
            const origin = await getGeo();
            const r = await gerarRotaInteligente({
                responsavelId: profile.id,
                data: iso,
                pontos: visitasARotear.map(v => ({ id: String(v.id), lat: v.negociacoes!.latitude!, lng: v.negociacoes!.longitude!, label: v.negociacoes?.titulo || v.titulo, negociacao_id: v.negociacao_id!, tarefa_id: v.id })),
                origin,
            });
            addToast('Rota Inteligente pronta! 🚗', 'success');
            await Promise.all([fetchRotas(), fetchVisitasDia(iso)]);
            setRotaAbertaId(r.rotaId);
        } catch (err) {
            console.error('Erro ao criar rota inteligente:', err);
            addToast('Erro ao criar a rota inteligente.', 'error');
        } finally {
            setGerandoRota(false);
        }
    };

    // Navegar a rota inteira no Google Maps (paradas pendentes, na ordem, a partir do meu GPS)
    const abrirNavegacaoCompleta = async () => {
        const rota = rotas.find(r => r.id === rotaAbertaId);
        if (!rota) return;
        const pend = rota.rota_paradas
            .filter(p => p.status !== 'visitado' && p.negociacoes?.latitude && p.negociacoes?.longitude)
            .sort((a, b) => a.posicao - b.posicao)
            .map(p => ({ lat: p.negociacoes!.latitude!, lng: p.negociacoes!.longitude! }));
        if (pend.length === 0) { addToast('Sem paradas pendentes com localização.', 'info'); return; }
        // Ponto de chegada da rota (quando definido na Prospecção). Ele ocupa uma das vagas
        // do Maps, então o teto de paradas cai para 9 — por isso o limite é calculado aqui.
        const destino = (rota as any).destino_lat != null && (rota as any).destino_lng != null
            ? { lat: Number((rota as any).destino_lat), lng: Number((rota as any).destino_lng) }
            : null;
        const teto = destino ? GOOGLE_MAPS_MAX_STOPS - 1 : GOOGLE_MAPS_MAX_STOPS;
        if (pend.length > teto) {
            addToast(`O Google Maps aceita ${teto} paradas por vez${destino ? ' (o ponto de chegada ocupa uma)' : ''} — abrindo as próximas ${teto}. Conclua visitas e abra de novo para continuar.`, 'info');
        }
        const origin = await getGeo();
        window.open(googleMapsDirLink(pend, origin, destino), '_blank', 'noopener');
    };

    // Re-ancora as paradas pendentes no GPS atual (meio do dia, fora da ordem planejada).
    const reordenarAPartirDaqui = async () => {
        if (!rotaAberta || reordenando) return;
        setReordenando(true);
        try {
            const origin = await getGeo();
            if (!origin) { addToast('Não consegui obter sua localização — ative o GPS e tente de novo.', 'warning'); return; }
            const n = await reordenarPendentesAPartirDe(rotaAberta.id, origin);
            addToast(n > 0 ? `Rota reordenada a partir da sua posição — ${n} paradas pendentes. 📍` : 'Nada para reordenar.', n > 0 ? 'success' : 'info');
            await fetchRotas();
        } catch (err) {
            console.error('Erro ao reordenar rota:', err);
            addToast('Erro ao reordenar a rota.', 'error');
        } finally {
            setReordenando(false);
        }
    };

    // Realtime: novas visitas atribuídas ao motoboy aparecem sem refresh manual.
    useAgendaRealtime({
        enabled: !!profile && !loading,
        onTarefasChange: () => { if (profile) { fetchRotas(); fetchVisitasDia(diaAlvo); } },
        onRotasChange: () => { if (profile) fetchRotas(); },
    });

    const paradaParaInfo = (p: ParadaOp): VisitaParadaInfo => ({
        id: p.id,
        titulo: p.negociacoes?.titulo || `Parada ${p.posicao}`,
        checkin_em: p.checkin_em,
        cidade: p.negociacoes?.cidade,
        bairro: p.negociacoes?.bairro,
        segmento: p.negociacoes?.segmento,
        ocrStatus: p.ocr_status,
        ocrCandidatos: p.ocr_candidatos,
        numeroMedidorLead: p.negociacoes?.numero_medidor,
        fotoUrl: p.foto_url,
    });

    // Com o modal de conferência aberto e o OCR ainda processando, busca o resultado a cada 3s
    useEffect(() => {
        if (!visitaModal || visitaModal.etapa !== 'encerrar' || visitaModal.parada.ocrStatus !== 'processando') return;
        const paradaId = visitaModal.parada.id;
        const checar = async () => {
            const { data } = await supabase.from('rota_paradas')
                .select('ocr_status, ocr_candidatos').eq('id', paradaId).maybeSingle();
            if (data && data.ocr_status !== 'processando') {
                setVisitaModal(prev => prev && prev.parada.id === paradaId
                    ? { ...prev, parada: { ...prev.parada, ocrStatus: data.ocr_status, ocrCandidatos: data.ocr_candidatos } }
                    : prev);
            }
        };
        checar();
        const t = setInterval(checar, 3000);
        return () => clearInterval(t);
    }, [visitaModal?.parada.id, visitaModal?.etapa, visitaModal?.parada.ocrStatus]);

    // ---------- Calendário + remanejamento ----------
    const resumoPorDia = useMemo(() => {
        const mapa: Record<string, DiaRotasResumo> = {};
        const hojeStr = hojeIso();
        for (const r of rotasMes) {
            const dia = mapa[r.data] || { rotas: 0, leads: 0, leadsConcluidos: 0, km: 0, emExecucao: false, concluida: true, atrasada: false };
            dia.rotas += 1;
            dia.leads += r.rota_paradas.length;
            dia.leadsConcluidos += r.rota_paradas.filter(p => p.status === 'visitado').length;
            dia.km += Number(r.distancia_total_km || 0);
            if (r.status === 'em_execucao') dia.emExecucao = true;
            if (r.status !== 'concluida') dia.concluida = false;
            if (r.data < hojeStr && r.status !== 'concluida' && r.rota_paradas.some(p => p.status !== 'visitado')) dia.atrasada = true;
            mapa[r.data] = dia;
        }
        return mapa;
    }, [rotasMes]);

    const rotasDoDia = useMemo(() => rotasMes.filter(r => r.data === dataSel), [rotasMes, dataSel]);

    /** Agenda futura (dias > hoje) usada pela sugestão inteligente de reagendamento. */
    const agendaFutura = useMemo((): DiaAgendaFutura[] => {
        const hojeStr = hojeIso();
        const porDia = new Map<string, PontoSugestao[]>();
        for (const r of rotasMes) {
            if (r.data <= hojeStr || ['concluida', 'cancelada'].includes(r.status)) continue;
            const leads = porDia.get(r.data) || [];
            for (const p of r.rota_paradas) {
                leads.push({ lat: p.negociacoes?.latitude, lng: p.negociacoes?.longitude, cidade: p.negociacoes?.cidade });
            }
            porDia.set(r.data, leads);
        }
        return Array.from(porDia.entries()).map(([data, leads]) => ({ data, leads }));
    }, [rotasMes]);

    const sugestaoRemanejo = useMemo(
        () => remanejo ? sugerirDataReagendamento(remanejo.paradas.map(p => p.ponto || {}), agendaFutura) : null,
        [remanejo, agendaFutura]
    );

    const paradaRemanejavel = (p: ParadaOp): ParadaRemanejavel & { ponto: PontoSugestao } => ({
        id: p.id,
        tarefa_id: p.tarefa_id ?? null,
        negociacao_id: p.negociacao_id ?? null,
        ponto: { lat: p.negociacoes?.latitude, lng: p.negociacoes?.longitude, cidade: p.negociacoes?.cidade },
    });

    const abrirRemanejoLeads = (rota: RotaOp, paradas: ParadaOp[]) => {
        if (paradas.length === 0) return;
        setRemanejo({
            modo: 'leads',
            rotaId: rota.id,
            titulo: rota.nome || `Rota de ${fmtData(rota.data)}`,
            dataOriginal: rota.data,
            paradas: paradas.map(paradaRemanejavel),
        });
    };

    const abrirRemanejoRota = (rota: RotaOp) => {
        const pendentes = rota.rota_paradas.filter(p => p.status !== 'visitado');
        setRemanejo({
            modo: 'rota',
            rotaId: rota.id,
            titulo: rota.nome || `Rota de ${fmtData(rota.data)}`,
            dataOriginal: rota.data,
            paradas: pendentes.map(paradaRemanejavel),
        });
    };

    const refreshAgenda = async () => {
        await Promise.all([fetchRotas(), vista === 'calendario' ? fetchRotasMes(mesAtual) : Promise.resolve()]);
    };

    const confirmarRemanejo = async (novaData: string, motivo: string) => {
        if (!remanejo || !profile) return;
        if (novaData === remanejo.dataOriginal) { setRemanejo(null); return; }
        setRemanejando(true);
        try {
            if (remanejo.modo === 'rota') {
                await remanejarRotaInteira({
                    rotaId: remanejo.rotaId, dataOriginal: remanejo.dataOriginal, novaData,
                    motivo, userId: profile.id, origem: 'operador',
                });
                addToast(`Rota movida para ${fmtData(novaData)} 📅`, 'success');
            } else {
                await remanejarParadas({
                    paradas: remanejo.paradas, rotaOrigemId: remanejo.rotaId, vendedorId: profile.id,
                    dataOriginal: remanejo.dataOriginal, novaData, motivo, userId: profile.id, origem: 'operador',
                });
                addToast(`${remanejo.paradas.length} lead(s) remanejado(s) para ${fmtData(novaData)} 📅`, 'success');
            }
            setRemanejo(null);
            setLeadsSel(new Set());
            setExpedienteRota(null);
            sessionStorage.setItem(`expediente-acao-${hojeIso()}`, '1');
            await refreshAgenda();
        } catch (err) {
            console.error('Erro ao remanejar:', err);
            addToast('Erro ao remanejar. Tente novamente.', 'error');
        } finally {
            setRemanejando(false);
        }
    };

    /** Drop nativo (HTML5): soltar um lead numa data do calendário remaneja na hora. */
    const soltarNoDia = async (novaData: string) => {
        if (!profile || !arrastando) return;
        const { rota, parada } = arrastando;
        setArrastando(null);
        if (novaData === rota.data || novaData < hojeIso()) return;
        setRemanejando(true);
        try {
            await remanejarParadas({
                paradas: [paradaRemanejavel(parada)], rotaOrigemId: rota.id, vendedorId: profile.id,
                dataOriginal: rota.data, novaData, motivo: null, userId: profile.id, origem: 'operador',
            });
            addToast(`${parada.negociacoes?.titulo || 'Lead'} movido para ${fmtData(novaData)} 📅`, 'success');
            setLeadsSel(prev => { const n = new Set(prev); n.delete(parada.id); return n; });
            await refreshAgenda();
        } catch (err) {
            console.error('Erro ao remanejar o lead:', err);
            addToast('Erro ao remanejar o lead.', 'error');
        } finally {
            setRemanejando(false);
        }
    };

    // ---------- Alerta de fim de expediente (item 3) ----------
    useEffect(() => {
        if (loading || !profile) return;
        const hojeStr = hojeIso();
        if (sessionStorage.getItem(`expediente-acao-${hojeStr}`)) return;
        const agora = new Date();
        const [h, m] = (fimExpediente || '18:00').split(':').map(Number);
        const passouExpediente = agora.getHours() > h || (agora.getHours() === h && agora.getMinutes() >= (m || 0));
        if (!passouExpediente) return;
        const rotaPendente = rotas.find(r =>
            r.data === hojeStr
            && ['planejada', 'agendada', 'reagendada', 'em_execucao'].includes(r.status)
            && r.rota_paradas.some(p => p.status !== 'visitado')
        );
        if (rotaPendente) setExpedienteRota(rotaPendente);
    }, [loading, rotas, fimExpediente, profile?.id]);

    const manterPendentes = async () => {
        if (!expedienteRota || !profile) return;
        const pendentes = expedienteRota.rota_paradas.filter(p => p.status !== 'visitado').map(paradaRemanejavel);
        await registrarManterPendentes(pendentes, expedienteRota.id, expedienteRota.data, profile.id);
        sessionStorage.setItem(`expediente-acao-${hojeIso()}`, '1');
        setExpedienteRota(null);
        addToast('Leads mantidos como pendentes na data original.', 'info');
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'manter_pendentes_expediente', origem: 'acao' });
    };

    const reagendarPendentesAmanha = async () => {
        if (!expedienteRota || !profile) return;
        const pendentes = expedienteRota.rota_paradas.filter(p => p.status !== 'visitado');
        const amanha = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
        setRemanejando(true);
        try {
            await remanejarParadas({
                paradas: pendentes.map(paradaRemanejavel), rotaOrigemId: expedienteRota.id, vendedorId: profile.id,
                dataOriginal: expedienteRota.data, novaData: amanha,
                motivo: 'Reagendado pelo alerta de fim de expediente', userId: profile.id, origem: 'alerta',
            });
            sessionStorage.setItem(`expediente-acao-${hojeIso()}`, '1');
            setExpedienteRota(null);
            addToast(`${pendentes.length} lead(s) reagendado(s) para amanhã 📅`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'reagendar_visita', origem: 'acao' });
            await refreshAgenda();
        } catch (err) {
            console.error(err);
            addToast('Erro ao reagendar os leads.', 'error');
        } finally {
            setRemanejando(false);
        }
    };

    // ---------- Ações ----------
    const iniciarRota = async (rota: RotaOp) => {
        const { error } = await supabase.from('rotas_comerciais')
            .update({ status: 'em_execucao', iniciada_em: rota.iniciada_em || new Date().toISOString() })
            .eq('id', rota.id);
        if (error) { addToast('Erro ao iniciar rota.', 'error'); return; }
        addToast(`Rota iniciada — ${rota.rota_paradas.length} paradas. Boa rota! 🛵`, 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'iniciar_rota', origem: 'acao' });
        await fetchRotas();
        setRotaAbertaId(rota.id);
    };

    /** Dispara o OCR do medidor em segundo plano (Google Vision via edge function). */
    const dispararOcr = (paradaId: string) => {
        supabase.functions.invoke('ocr-medidor', { body: { parada_id: paradaId } })
            .catch(err => console.error('ocr-medidor:', err));
    };

    const iniciarVisita = async (info: VisitaParadaInfo, foto: File) => {
        if (!rotaAberta || !profile) return;
        try {
            const geo = await getGeo();
            const ext = (foto.name.split('.').pop() || 'jpg').toLowerCase();
            const path = `${rotaAberta.id}/${info.id}_${Date.now()}.${ext}`;
            const { error: upErr } = await supabase.storage.from('visitas').upload(path, foto);
            if (upErr) throw upErr;
            const { data: { publicUrl } } = supabase.storage.from('visitas').getPublicUrl(path);

            const { error } = await supabase.from('rota_paradas')
                .update({
                    status: 'em_visita', checkin_em: new Date().toISOString(), foto_url: publicUrl,
                    checkin_lat: geo?.lat ?? null, checkin_lng: geo?.lng ?? null,
                    ocr_status: 'processando', ocr_candidatos: null,
                })
                .eq('id', info.id);
            if (error) throw error;
            dispararOcr(info.id);
            setVisitaModal(null);
            addToast('Foto registrada! Confirme a leitura ao sair. 📸', 'success');
            await fetchRotas();
        } catch (err) {
            console.error(err);
            addToast('Erro ao registrar a foto. Tente novamente.', 'error');
        }
    };

    /** Refaz a foto durante a conferência: novo upload + novo OCR na mesma parada. */
    const refazerFoto = async (info: VisitaParadaInfo, foto: File) => {
        if (!rotaAberta || !profile) return;
        try {
            const ext = (foto.name.split('.').pop() || 'jpg').toLowerCase();
            const path = `${rotaAberta.id}/${info.id}_${Date.now()}.${ext}`;
            const { error: upErr } = await supabase.storage.from('visitas').upload(path, foto);
            if (upErr) throw upErr;
            const { data: { publicUrl } } = supabase.storage.from('visitas').getPublicUrl(path);
            const { error } = await supabase.from('rota_paradas')
                .update({ foto_url: publicUrl, ocr_status: 'processando', ocr_candidatos: null })
                .eq('id', info.id);
            if (error) throw error;
            dispararOcr(info.id);
            setVisitaModal(prev => prev ? { ...prev, parada: { ...prev.parada, fotoUrl: publicUrl, ocrStatus: 'processando', ocrCandidatos: null } } : prev);
            await fetchRotas();
        } catch (err) {
            console.error(err);
            addToast('Erro ao enviar a nova foto.', 'error');
        }
    };

    const encerrarVisita = async (info: VisitaParadaInfo, dados: VisitaConclusao) => {
        if (!rotaAberta || !profile) return;
        const parada = rotaAberta.rota_paradas.find(p => p.id === info.id);
        try {
            const geo = await getGeo();
            const agora = new Date().toISOString();
            const numMedidor = dados.numeroMedidor || null;
            const { error } = await supabase.from('rota_paradas')
                .update({
                    status: 'visitado', checkout_em: agora, visitado_em: agora,
                    descricao_visita: dados.descricao || null,
                    checkout_lat: geo?.lat ?? null, checkout_lng: geo?.lng ?? null,
                    numero_medidor: numMedidor,
                    medidor_confirmado_em: numMedidor ? agora : null,
                    medidor_confirmado_por: numMedidor ? profile.id : null,
                })
                .eq('id', info.id);
            if (error) throw error;
            logUsageEvent({ userId: profile.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'encerrar_leitura_medidor', origem: 'acao' });

            if (parada?.tarefa_id) {
                // Observação da leitura fica na descrição da tarefa → aparece no card da agenda
                await concluirTarefaComResultado(parada.tarefa_id, dados.descricao);
            }
            if (parada?.negociacao_id) {
                // Vincula o medidor ao cadastro do lead (evita nova coleta futura)
                if (numMedidor) {
                    await supabase.from('negociacoes').update({
                        numero_medidor: numMedidor,
                        medidor_foto_url: parada.foto_url || null,
                        medidor_capturado_em: agora,
                        medidor_capturado_por: profile.id,
                    }).eq('id', parada.negociacao_id);
                }
                const inicio = parada.checkin_em ? new Date(parada.checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '?';
                const fim = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                await supabase.from('negociacao_notas').insert([{
                    negociacao_id: parada.negociacao_id,
                    user_id: profile.id,
                    conteudo: `📸 Leitura de medidor registrada (${new Date().toLocaleDateString('pt-BR')} · ${inicio} → ${fim})${numMedidor ? ` — Medidor nº ${numMedidor}` : ''}${dados.descricao ? ` — ${dados.descricao}` : ''}`,
                    tipo: 'nota',
                }]);
            }
            setVisitaModal(null);
            const proxima = rotaAberta.rota_paradas
                .filter(p => p.id !== info.id && p.status === 'pendente')
                .sort((a, b) => a.posicao - b.posicao)[0];
            addToast(
                proxima ? `Leitura concluída! ✅ Próximo: ${proxima.negociacoes?.titulo || `Parada ${proxima.posicao}`}` : 'Leitura concluída! ✅ Última parada — finalize a rota 🎉',
                'success',
                proxima ? 8000 : 4000,
                urlNavegacao(proxima?.negociacoes)
                    ? {
                        label: '🧭 Navegar',
                        onClick: () => window.open(urlNavegacao(proxima!.negociacoes), '_blank', 'noopener'),
                    }
                    : undefined
            );
            await fetchRotas();
        } catch (err) {
            console.error(err);
            addToast('Erro ao concluir a leitura.', 'error');
        }
    };

    const finalizarRota = async () => {
        if (!rotaAberta) return;
        const { error } = await supabase.from('rotas_comerciais')
            .update({ status: 'concluida', concluida_em: new Date().toISOString() })
            .eq('id', rotaAberta.id);
        if (error) { addToast('Erro ao finalizar a rota.', 'error'); return; }
        setShowResumo(false);
        setRotaAbertaId(null);
        addToast('Rota finalizada! Bom trabalho 🎉', 'success');
        await fetchRotas();
    };

    // ---------- Render ----------
    if (loading) {
        return <div className="flex items-center justify-center h-64"><LoadingSpinner label="Carregando suas rotas..." size="sm" /></div>;
    }

    // ===== Tela de execução de uma rota =====
    if (rotaAberta) {
        const visitadas = rotaAberta.rota_paradas.filter(p => p.status === 'visitado');
        const fotos = rotaAberta.rota_paradas.filter(p => p.foto_url).length;
        const proximaPendente = rotaAberta.rota_paradas.find(p => p.status === 'pendente');
        const todasFeitas = visitadas.length === rotaAberta.rota_paradas.length;
        const obsRegistradas = rotaAberta.rota_paradas.filter(p => p.descricao_visita);
        // Visão completa da rota no mapa embutido (o Google Maps corta em 10 por link).
        const paradasMapa: ParadaMapa[] = rotaAberta.rota_paradas
            .filter(p => p.negociacoes?.latitude && p.negociacoes?.longitude)
            .map(p => ({
                id: p.id, posicao: p.posicao, status: p.status,
                lat: p.negociacoes!.latitude!, lng: p.negociacoes!.longitude!,
                titulo: p.negociacoes?.titulo || `Parada ${p.posicao}`,
                endereco: [p.negociacoes?.bairro, p.negociacoes?.cidade].filter(Boolean).join(', ') || undefined,
            }));
        const semGeo = rotaAberta.rota_paradas.length - paradasMapa.length;
        const proximaNav = [...rotaAberta.rota_paradas].sort((a, b) => a.posicao - b.posicao)
            .find(p => p.status === 'pendente' && p.negociacoes?.latitude && p.negociacoes?.longitude) || null;

        return (
            <div className="max-w-lg mx-auto px-2 sm:px-0 space-y-3 pb-8">
                <div className="flex items-center gap-3">
                    <button onClick={() => setRotaAbertaId(null)} className="p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500">
                        <ArrowLeft size={18} />
                    </button>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-base font-bold text-gray-900 dark:text-white truncate">{rotaAberta.nome || `Rota de ${fmtData(rotaAberta.data)}`}</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {visitadas.length}/{rotaAberta.rota_paradas.length} concluídas · {fotos} fotos
                            {rotaAberta.iniciada_em && ` · ${tempoTotal(rotaAberta.iniciada_em, rotaAberta.concluida_em)}`}
                        </p>
                    </div>
                    {statusChip(rotaAberta.status)}
                </div>

                {rotaAberta.observacoes && (
                    <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                        <p className="text-xs text-amber-800 dark:text-amber-300"><b>Instruções do gestor:</b> {rotaAberta.observacoes}</p>
                    </div>
                )}

                <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${rotaAberta.rota_paradas.length > 0 ? (visitadas.length / rotaAberta.rota_paradas.length) * 100 : 0}%` }}></div>
                </div>

                {paradasMapa.length > 0 && (
                    <button
                        onClick={() => setMapaAberto(v => !v)}
                        className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
                    >
                        <MapIcon size={12} /> {mapaAberto ? 'Ocultar mapa' : `Ver mapa da rota (${paradasMapa.length})`}
                    </button>
                )}
                {mapaAberto && (
                    <Suspense fallback={<div className="h-56 sm:h-72 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse" />}>
                        <RotaMapa paradas={paradasMapa} semGeo={semGeo} />
                    </Suspense>
                )}

                {rotaAberta.status !== 'em_execucao' && rotaAberta.status !== 'concluida' && (
                    <button onClick={() => iniciarRota(rotaAberta)} className="w-full flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-bold rounded-xl bg-primary text-white">
                        <RouteIcon size={16} /> Iniciar Rota ({rotaAberta.rota_paradas.length} paradas)
                    </button>
                )}

                <div className="space-y-2.5">
                    {rotaAberta.rota_paradas.map(p => {
                        const ehProxima = proximaPendente?.id === p.id && rotaAberta.status === 'em_execucao';
                        const neg = p.negociacoes;
                        return (
                            <div key={p.id} className={`p-4 rounded-2xl border bg-white dark:bg-gray-800 ${p.status === 'visitado' ? 'border-emerald-200 dark:border-emerald-800 opacity-80' : ehProxima ? 'border-primary ring-2 ring-primary/20' : p.status === 'em_visita' ? 'border-blue-300 dark:border-blue-700 ring-2 ring-blue-200/50' : 'border-gray-100 dark:border-gray-700'}`}>
                                <div className="flex items-start gap-3">
                                    <span className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${p.status === 'visitado' ? 'bg-emerald-500 text-white' : p.status === 'em_visita' ? 'bg-blue-500 text-white animate-pulse' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                        {p.status === 'visitado' ? '✓' : p.posicao}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <p className={`text-sm font-bold ${p.status === 'visitado' ? 'text-emerald-700 dark:text-emerald-300 line-through' : 'text-gray-900 dark:text-white'}`}>{neg?.titulo || `Parada ${p.posicao}`}</p>
                                            {ehProxima && <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-primary text-white uppercase">Próxima</span>}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{enderecoParada(neg) || 'Sem endereço'}{neg?.segmento ? ` · ${neg.segmento}` : ''}</p>
                                        {(p.numero_medidor || neg?.numero_medidor) && (
                                            <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 mt-0.5">
                                                🔢 Medidor {p.numero_medidor ? `confirmado: ${p.numero_medidor}` : `já coletado: ${neg!.numero_medidor}`}
                                            </p>
                                        )}
                                        {p.descricao_visita && <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">📝 {p.descricao_visita}</p>}

                                        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                                            {urlNavegacao(neg) ? (
                                                <a href={urlNavegacao(neg)} target="_blank" rel="noopener noreferrer"
                                                    className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                    <Navigation size={11} /> Navegar
                                                </a>
                                            ) : null}
                                            {neg?.whatsapp && (
                                                <a href={`tel:${neg.whatsapp.replace(/\D/g, '')}`} className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                    <Phone size={11} /> Ligar
                                                </a>
                                            )}
                                            {rotaAberta.status === 'em_execucao' && p.status === 'pendente' && (
                                                <button
                                                    onClick={() => setVisitaModal({ parada: paradaParaInfo(p), etapa: 'iniciar' })}
                                                    className="flex items-center gap-1.5 px-3.5 py-1.5 text-[11px] font-bold rounded-full bg-primary text-white ml-auto"
                                                >
                                                    <Camera size={12} /> Tirar Foto
                                                </button>
                                            )}
                                            {rotaAberta.status === 'em_execucao' && p.status === 'em_visita' && (
                                                <button
                                                    onClick={() => setVisitaModal({ parada: paradaParaInfo(p), etapa: 'encerrar' })}
                                                    className="flex items-center gap-1.5 px-3.5 py-1.5 text-[11px] font-bold rounded-full bg-emerald-500 text-white ml-auto"
                                                >
                                                    <CheckCircle2 size={12} /> Confirmar Leitura
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {rotaAberta.status === 'em_execucao' && proximaNav && (
                    <a
                        href={urlNavegacao(proximaNav.negociacoes)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                        <Navigation size={15} /> Navegar p/ próxima parada (#{proximaNav.posicao})
                    </a>
                )}
                {rotaAberta.status === 'em_execucao' && rotaAberta.rota_paradas.filter(p => p.status === 'pendente' && p.negociacoes?.latitude && p.negociacoes?.longitude).length >= 2 && (
                    <button
                        onClick={reordenarAPartirDaqui}
                        disabled={reordenando}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                        <LocateFixed size={15} /> {reordenando ? 'Reordenando…' : 'Reordenar a partir de onde estou'}
                    </button>
                )}
                {rotaAberta.rota_paradas.some(p => p.status !== 'visitado' && p.negociacoes?.latitude) && (
                    <button
                        onClick={abrirNavegacaoCompleta}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                        <ExternalLink size={15} /> Navegar rota completa
                    </button>
                )}

                {rotaAberta.status === 'em_execucao' && (
                    <button
                        onClick={() => setShowResumo(true)}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-bold rounded-xl text-white ${todasFeitas ? 'bg-emerald-500' : 'bg-emerald-500/80'}`}
                    >
                        <Flag size={16} /> Finalizar Rota {todasFeitas ? '' : `(${visitadas.length}/${rotaAberta.rota_paradas.length})`}
                    </button>
                )}

                {visitaModal && (
                    <VisitaModal
                        parada={visitaModal.parada}
                        etapa={visitaModal.etapa}
                        modo="foto"
                        onClose={() => setVisitaModal(null)}
                        onIniciar={iniciarVisita}
                        onEncerrar={encerrarVisita}
                        onRefazerFoto={refazerFoto}
                    />
                )}

                {/* Resumo de finalização */}
                {showResumo && createPortal(
                    <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={() => setShowResumo(false)}>
                        <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-5" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-sm font-bold text-gray-900 dark:text-white">Resumo da Rota</h2>
                                <button onClick={() => setShowResumo(false)} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mb-4">
                                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3 text-center">
                                    <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{visitadas.length}/{rotaAberta.rota_paradas.length}</p>
                                    <p className="text-[10px] font-semibold text-emerald-600/70">Concluídas</p>
                                </div>
                                <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3 text-center">
                                    <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{fotos}</p>
                                    <p className="text-[10px] font-semibold text-blue-600/70">Fotos</p>
                                </div>
                                <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-3 text-center">
                                    <p className="text-lg font-bold text-gray-700 dark:text-gray-200">{tempoTotal(rotaAberta.iniciada_em) || '—'}</p>
                                    <p className="text-[10px] font-semibold text-gray-400">Tempo total</p>
                                </div>
                            </div>
                            {obsRegistradas.length > 0 && (
                                <div className="mb-4 max-h-32 overflow-y-auto space-y-1">
                                    {obsRegistradas.map(p => (
                                        <p key={p.id} className="text-[11px] text-gray-500 dark:text-gray-400">📝 <b>{p.negociacoes?.titulo}:</b> {p.descricao_visita}</p>
                                    ))}
                                </div>
                            )}
                            {!todasFeitas && (
                                <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-3">⚠️ Ainda há {rotaAberta.rota_paradas.length - visitadas.length} parada(s) sem leitura. Ao finalizar, a rota não recebe mais capturas.</p>
                            )}
                            <button onClick={finalizarRota} className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-emerald-500 text-white">
                                <Flag size={15} /> Confirmar Finalização
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    // ===== Lista "Minhas Rotas" =====
    const Secao: React.FC<{ titulo: string; lista: RotaOp[]; vazio?: string }> = ({ titulo, lista, vazio }) => (
        <div>
            <h2 className="text-xs font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">{titulo}</h2>
            {lista.length === 0 ? (
                vazio ? <p className="text-xs text-gray-400 mb-2">{vazio}</p> : null
            ) : (
                <div className="space-y-2.5">
                    {lista.map(r => {
                        const visitadas = r.rota_paradas.filter(p => p.status === 'visitado').length;
                        return (
                            <button
                                key={r.id}
                                onClick={() => setRotaAbertaId(r.id)}
                                className="w-full text-left p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm active:scale-[0.99] transition-transform"
                            >
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{r.nome || `Rota de ${fmtData(r.data)}`}</p>
                                    {statusChip(r.status)}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap text-[11px] text-gray-500 dark:text-gray-400">
                                    <span className="flex items-center gap-1"><Clock size={11} /> {fmtData(r.data)}</span>
                                    <span>{visitadas}/{r.rota_paradas.length} paradas</span>
                                    {(r.distancia_total_km || 0) > 0 && <span>~{Math.round(r.distancia_total_km!)} km</span>}
                                    {prioridadeChip(r.prioridade)}
                                </div>
                                {r.observacoes && <p className="text-[11px] text-gray-400 mt-1 truncate italic">"{r.observacoes}"</p>}
                                <div className="flex items-center justify-end mt-1 text-primary text-[11px] font-bold">
                                    {r.status === 'em_execucao' ? 'Continuar' : r.status === 'concluida' ? 'Ver detalhes' : 'Abrir rota'} <ChevronRight size={13} />
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const fmtDiaLongo = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

    // Visitas planejadas do dia ainda sem rota → botão único "Criar Rota Inteligente"
    const renderVisitasARotear = (iso: string, titulo: string) => {
        if (visitasARotear.length === 0) return null;
        return (
            <div className="p-4 rounded-2xl bg-indigo-50/60 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-900/40 space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                    <h2 className="text-xs font-extrabold text-indigo-500 dark:text-indigo-400 uppercase tracking-widest">{titulo}</h2>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">{visitasARotear.length}</span>
                </div>
                <div className="space-y-1.5">
                    {visitasARotear.map(v => (
                        <div key={v.id} className="flex items-center gap-2 p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                            <MapPin size={13} className="text-indigo-500 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{v.negociacoes?.titulo || v.titulo}</p>
                                <p className="text-[10px] text-gray-400 truncate">{[v.negociacoes?.bairro, v.negociacoes?.cidade].filter(Boolean).join(', ') || '—'}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    onClick={() => criarRotaInteligenteDia(iso)}
                    disabled={gerandoRota}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                >
                    <Navigation size={15} /> {gerandoRota ? 'Gerando...' : `Criar Rota Inteligente (${visitasARotear.length})`}
                </button>
            </div>
        );
    };

    return (
        <div className={`${vista === 'calendario' ? 'max-w-3xl' : 'max-w-lg'} mx-auto px-2 sm:px-0 space-y-5 pb-8`}>
            <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl">
                    <RouteIcon size={22} />
                </div>
                <div className="min-w-0 flex-1">
                    <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Minhas Rotas</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Olá, {profile?.full_name?.split(' ')[0]}! Suas rotas de campo.</p>
                </div>
                {/* Toggle Lista | Calendário */}
                <div className="flex items-center gap-0.5 p-0.5 rounded-full bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 shrink-0">
                    <button
                        onClick={() => setVista('lista')}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-all ${vista === 'lista' ? 'bg-white dark:bg-gray-800 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    >
                        <List size={12} /> Lista
                    </button>
                    <button
                        onClick={() => setVista('calendario')}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-all ${vista === 'calendario' ? 'bg-white dark:bg-gray-800 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    >
                        <CalendarDays size={12} /> Calendário
                    </button>
                </div>
            </div>

            {vista === 'lista' ? (
                <>
                    {grupos.emExecucao.length > 0 && <Secao titulo="▶ Em Execução" lista={grupos.emExecucao} />}
                    {renderVisitasARotear(hojeIso(), 'Visitas de hoje a rotear')}
                    <Secao titulo="📍 Hoje" lista={grupos.doDia} vazio="Nenhuma rota para hoje." />
                    <Secao titulo="🗓️ Próximas" lista={grupos.futuras} vazio="Nenhuma rota futura agendada." />
                    {grupos.concluidas.length > 0 && <Secao titulo="✅ Concluídas" lista={grupos.concluidas} />}

                    {rotas.length === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            <ExternalLink size={36} className="mx-auto mb-3 opacity-30" />
                            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Nenhuma rota atribuída a você ainda.</p>
                            <p className="text-xs mt-1">Quando o gestor criar uma rota para você, ela aparece aqui.</p>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <CalendarioRotas
                        mes={mesAtual}
                        onChangeMes={setMesAtual}
                        dataSel={dataSel}
                        onSelectDate={d => { setDataSel(d); setLeadsSel(new Set()); }}
                        resumoPorDia={resumoPorDia}
                        onDropDia={soltarNoDia}
                        arrastando={!!arrastando}
                    />

                    {renderVisitasARotear(dataSel, 'Visitas a rotear neste dia')}

                    {/* Rotas do dia selecionado */}
                    <div className="space-y-2.5">
                        <h2 className="text-xs font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest capitalize">
                            📍 {fmtDiaLongo(dataSel)}
                        </h2>
                        {rotasDoDia.length === 0 ? (
                            <p className="text-xs text-gray-400">Nenhuma rota nesta data.</p>
                        ) : rotasDoDia.map(rota => {
                            const pendentes = rota.rota_paradas.filter(p => p.status !== 'visitado');
                            const visitadasQtd = rota.rota_paradas.length - pendentes.length;
                            const selNaRota = pendentes.filter(p => leadsSel.has(p.id));
                            const podeRemanejar = !['concluida', 'cancelada'].includes(rota.status);
                            return (
                                <div key={rota.id} className="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm space-y-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{rota.nome || `Rota de ${fmtData(rota.data)}`}</p>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                {visitadasQtd}/{rota.rota_paradas.length} concluídas
                                                {(rota.distancia_total_km || 0) > 0 && ` · ~${Math.round(rota.distancia_total_km!)} km`}
                                            </p>
                                        </div>
                                        {statusChip(rota.status)}
                                    </div>

                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <button
                                            onClick={() => setRotaAbertaId(rota.id)}
                                            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-full bg-primary/10 text-primary"
                                        >
                                            Abrir rota <ChevronRight size={12} />
                                        </button>
                                        {podeRemanejar && (
                                            <button
                                                onClick={() => abrirRemanejoRota(rota)}
                                                className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-full bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
                                            >
                                                <CalendarClock size={12} /> Alterar data da rota
                                            </button>
                                        )}
                                        {selNaRota.length > 0 && (
                                            <button
                                                onClick={() => abrirRemanejoLeads(rota, selNaRota)}
                                                className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-full bg-orange-500 text-white ml-auto"
                                            >
                                                <CalendarClock size={12} /> Remanejar {selNaRota.length} selecionado{selNaRota.length > 1 ? 's' : ''}
                                            </button>
                                        )}
                                    </div>

                                    {/* Leads da rota: arrastáveis (drag nativo) p/ outra data + seleção múltipla */}
                                    <div className="space-y-1.5">
                                        {rota.rota_paradas.map(p => {
                                            const visitado = p.status === 'visitado';
                                            const arrastavel = !visitado && podeRemanejar;
                                            const sendoArrastado = arrastando?.parada.id === p.id;
                                            return (
                                                <div
                                                    key={p.id}
                                                    draggable={arrastavel}
                                                    onDragStart={arrastavel ? (e => {
                                                        e.dataTransfer.setData('text/plain', p.id);
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        setArrastando({ rota, parada: p });
                                                    }) : undefined}
                                                    onDragEnd={() => setArrastando(null)}
                                                    className={`flex items-center gap-2 p-2 rounded-xl border text-left transition-opacity ${arrastavel ? 'cursor-grab active:cursor-grabbing' : ''} ${sendoArrastado ? 'opacity-40 border-primary ring-2 ring-primary/30' : visitado ? 'border-emerald-100 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-900/10 opacity-70' : 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30'}`}
                                                >
                                                    {arrastavel ? (
                                                        <>
                                                            <span className="text-gray-300 dark:text-gray-600 shrink-0" title="Arraste para outra data do calendário">
                                                                <GripVertical size={14} />
                                                            </span>
                                                            <input
                                                                type="checkbox"
                                                                checked={leadsSel.has(p.id)}
                                                                onChange={e => setLeadsSel(prev => {
                                                                    const n = new Set(prev);
                                                                    if (e.target.checked) n.add(p.id); else n.delete(p.id);
                                                                    return n;
                                                                })}
                                                                className="rounded border-gray-300 text-primary focus:ring-primary/40 shrink-0"
                                                            />
                                                        </>
                                                    ) : (
                                                        <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${visitado ? 'bg-emerald-500 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300'}`}>
                                                            {visitado ? '✓' : p.posicao}
                                                        </span>
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <p className={`text-xs font-semibold truncate ${visitado ? 'text-emerald-700 dark:text-emerald-300 line-through' : 'text-gray-800 dark:text-gray-100'}`}>
                                                            {p.negociacoes?.titulo || `Parada ${p.posicao}`}
                                                        </p>
                                                        <p className="text-[10px] text-gray-400 truncate">{[p.negociacoes?.bairro, p.negociacoes?.cidade].filter(Boolean).join(', ') || '—'}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Remanejamento (leads selecionados / rota inteira / alerta) */}
            {remanejo && (
                <RemanejarLeadsModal
                    modo={remanejo.modo}
                    titulo={remanejo.titulo}
                    count={remanejo.paradas.length}
                    dataAtual={remanejo.dataOriginal}
                    sugestao={sugestaoRemanejo}
                    salvando={remanejando}
                    onConfirm={confirmarRemanejo}
                    onClose={() => setRemanejo(null)}
                />
            )}

            {/* Alerta de fim de expediente (item 3): rota do dia com visitas pendentes */}
            {expedienteRota && !remanejo && (() => {
                const total = expedienteRota.rota_paradas.length;
                const feitas = expedienteRota.rota_paradas.filter(p => p.status === 'visitado').length;
                const pend = total - feitas;
                return createPortal(
                    <div className="fixed inset-0 z-[1900] flex items-end sm:items-center justify-center sm:p-4 bg-black/50">
                        <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-5 space-y-4">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 shrink-0">
                                    <AlarmClock size={18} />
                                </div>
                                <div className="min-w-0">
                                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">Rota não concluída</h2>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{expedienteRota.nome || `Rota de ${fmtData(expedienteRota.data)}`}</p>
                                </div>
                                <button onClick={() => setExpedienteRota(null)} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 ml-auto shrink-0"><X size={18} /></button>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-200">
                                Você concluiu <b>{feitas} de {total}</b> visitas. Restam <b>{pend} lead{pend > 1 ? 's' : ''} pendente{pend > 1 ? 's' : ''}</b>.
                                <br /><span className="text-xs text-gray-500 dark:text-gray-400">Deseja reagendar os leads restantes?</span>
                            </p>
                            <div className="space-y-2">
                                <button
                                    onClick={reagendarPendentesAmanha}
                                    disabled={remanejando}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 disabled:opacity-40"
                                >
                                    <CalendarClock size={15} /> Reagendar para amanhã
                                </button>
                                <button
                                    onClick={() => abrirRemanejoLeads(expedienteRota, expedienteRota.rota_paradas.filter(p => p.status !== 'visitado'))}
                                    disabled={remanejando}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
                                >
                                    <CalendarDays size={15} /> Escolher outra data
                                </button>
                                <button
                                    onClick={manterPendentes}
                                    disabled={remanejando}
                                    className="w-full px-4 py-2.5 text-xs font-semibold rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40"
                                >
                                    Manter pendente na data original
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                );
            })()}
        </div>
    );
};

export default OperadorView;
